import { Alert } from "@material-tailwind/react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { ConfrimModal } from "./Confrim";
import store from "@/utils/store";
import { useHttp } from "@/hooks/useFetch";
import axios from "axios";
import { LoginDialog } from "../common/Login";
import { RegisterDialog } from "../common/Register";
import * as utils from "@/utils";
import { preventCopy } from "@/utils/preventCopy";
import { getWebsite } from "@/utils";

type ConfrimMessage = {
  title: React.ReactNode;
  message: React.ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
  footer?: boolean;
  showCancel?: boolean;
};

type LoginCallback = {
  onSuccess?: () => void;
};

type AlertMessage = {
  message: string;
  color: "blue" | "red" | "green" | "amber" | undefined;
};

export type GlobalContextType = {
  userInfo?: UserInfo;
  setUserInfo?: (userInfo: UserInfo) => void;
  getUserInfo: () => void;
  loginModal: boolean;
  setLoginModal: (loginModal: boolean) => void;
  registerModal: boolean;
  setRegisterModal: (loginModal: boolean) => void;
  token: string;
  setToken: (token: string) => void;
  alert: (content: AlertMessage) => void;
  confrim: (message: ConfrimMessage) => void;
  confrimInfo?: ConfrimMessage;
  confirmModal: boolean;
  setConfirmModal: (confirmModal: boolean) => void;
  login: (phone: string, password: string, authCode?: string) => void;
  logout: () => void;
  registerCallback?: LoginCallback;
  handleRegister: (callback: LoginCallback) => void;
  utils: typeof utils;
  appInfo: AppInfo | undefined;
  showChat: boolean;
  setShowChat: (showChat: boolean) => void;
  audioRef: React.RefObject<HTMLAudioElement>;
  showGlobalCodeModal: boolean;
  setShowGlobalCodeModal: (show: boolean) => void;
};

export const GlobalContext = createContext<GlobalContextType>({
  token: "",
  setToken: () => {},
  loginModal: false,
  getUserInfo: () => {},
  setLoginModal: () => {},
  registerModal: false,
  setRegisterModal: () => {},
  alert: () => {},
  confrim: () => {},
  confirmModal: false,
  setConfirmModal: () => {},
  login: () => {},
  logout: () => {},
  registerCallback: {},
  handleRegister: () => {},
  utils,
  appInfo: undefined,
  setShowChat: () => {},
  showChat: false,
  audioRef: {} as React.RefObject<HTMLAudioElement>,
  showGlobalCodeModal: false,
  setShowGlobalCodeModal: () => {},
});

export const GlobalContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { Http } = useHttp();

  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [loginModal, setLoginModal] = useState<boolean>(false);
  const [registerModal, setRegisterModal] = useState<boolean>(false);
  const [token, setToken] = useState<string>(store.getItem("token") || "");
  const audioRef = useRef<HTMLAudioElement>(null);
  const [alertInfo, setAlertInfo] = useState<AlertMessage>();
  const [confrimInfo, setConfrimInfo] = useState<ConfrimMessage>();
  const [confirmModal, setConfirmModal] = useState<boolean>(false);

  const [registerCallback, setRegisterCallback] = useState<LoginCallback>();
  const [appInfo, setAppInfo] = useState<AppInfo>();
  const [showChat, setShowChat] = useState<boolean>(false);
  const [showVipExpireWarn, setShowVipExpireWarn] = useState(false);
  const [vipExpireNoTipToday, setVipExpireNoTipToday] = useState(false);
  const [showGlobalCodeModal, setShowGlobalCodeModal] = useState(false);

  // 检查会员到期时间并弹窗
  useEffect(() => {
    if (!userInfo || !userInfo.member?.isValid || !userInfo.member?.endDate)
      return;
    // 只对有会员且有到期时间的用户判断
    const endDate = new Date(userInfo.member.endDate);
    const now = new Date();
    const diff = (endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    if (diff > 0 && diff <= 7) {
      // 检查今日是否已勾选不再提示
      const today = now.toISOString().slice(0, 10); // yyyy-mm-dd
      const key = `vip_expire_warn_${userInfo.id}_${today}`;
      const noTip = store.getItem(key);
      if (!noTip) {
        setShowVipExpireWarn(true);
      }
    }
  }, [userInfo]);

  useEffect(() => {
    if (["man", "travel"].includes(getWebsite())) {
      preventCopy();
    }
  }, []);

  const checkVersion = async () => {
    if (document.visibilityState != "visible") {
      return setTimeout(() => {
        checkVersion();
      }, 60 * 1000);
    }
    const version = store.getItem("version");
    const { data } = await axios.get("/version.json?t=" + Date.now());

    // if (true) {
    if (version !== data.version) {
      confrim({
        title: <div className="text-center">版本更新</div>,
        message: (
          <div className="text-center ">
            <p className="text-gray-800 text-md font-medium ">
              检测到新版本，是否更新？
            </p>
            <p className="text-sm font-medium mt-3 text-gray-800">
              {data.info}
            </p>
          </div>
        ),
        onConfirm: () => {
          store.setItem("version", data.version);
          window.location.reload();
        },
      });
    } else {
      setTimeout(() => {
        checkVersion();
      }, 60 * 1000);
    }
  };

  async function handleRegister(callbackOption: LoginCallback) {
    setRegisterCallback(callbackOption);
    setRegisterModal(true);
  }

  const initVersion = async () => {
    const { data } = await axios.get("/version.json?t=" + Date.now());
    store.setItem("version", data.version);
    setTimeout(() => {
      checkVersion();
    }, 60 * 1000);
  };

  const confrim = (message: ConfrimMessage) => {
    setConfrimInfo(message);
    setConfirmModal(true);
  };

  const alert = (content: AlertMessage) => {
    console.log(content);
    setAlertInfo(content);
    setTimeout(() => {
      setAlertInfo(undefined);
    }, 3000);
  };
  const getUserInfo = async () => {
    const userInfo: UserInfo = await Http.get("/app/user/info/person?member=1");
    userInfo.avatarUrl = userInfo.avatarUrl || import.meta.env.VITE_AVATAR;
    setUserInfo(userInfo);
    store.setItem("userInfo", userInfo);
  };

  const login = async (phone: string, password: string, authCode?: string) => {
    try {
      const data: any = await Http.post("/app/user/login/password", {
        phone,
        password,
        authCode,
      });
      store.setItem("token", data.token);
      setToken(data.token);

      await getUserInfo();

      registerCallback?.onSuccess && registerCallback.onSuccess();
      return userInfo;
    } catch (error: any) {
      alert({
        color: "red",
        message: error.message,
      });
      return Promise.reject(error);
    }
  };

  const logout = async () => {
    // Todo: 调用接口登出
    store.removeItem("token");
    store.removeItem("userInfo");
    setToken("");
    setShowChat(false);
    setUserInfo(undefined);
  };

  async function initUserInfo() {
    if (!store.getItem("token")) return;
    // setUserInfo(store.getItem("userInfo") as UserInfo);
    getUserInfo();
  }

  async function getAppInfo() {
    const data: AppInfo = await Http.get("/app/cms/manage/app", {
      params: { catch: 60 },
    });
    setAppInfo({
      ...data,
      // 后端可能返回 null/undefined，前端统一兜底成数组避免运行时崩溃
      navigationMenu: Array.isArray((data as any)?.navigationMenu)
        ? data.navigationMenu
        : [],
    });
  }

  useEffect(() => {
    // initVersion();
    initUserInfo();
    getAppInfo();
  }, []);

  useEffect(() => {
    if (appInfo) {
      document.title = appInfo.websiteCn;
    }
  }, [appInfo]);

  function todayNoTip() {
    if (vipExpireNoTipToday && userInfo) {
      const today = new Date().toISOString().slice(0, 10);
      const key = `vip_expire_warn_${userInfo.id}_${today}`;
      store.setItem(key, true);
    }
    setShowVipExpireWarn(false);
  }

  return (
    <>
      <GlobalContext.Provider
        value={{
          userInfo,
          setUserInfo,
          getUserInfo,
          loginModal,
          setLoginModal,
          registerModal,
          setRegisterModal,
          token,
          setToken,
          alert,
          confrim,
          confrimInfo,
          confirmModal,
          setConfirmModal,
          login,
          logout,
          registerCallback,
          handleRegister,
          utils,
          appInfo,
          showChat,
          setShowChat,
          audioRef,
          showGlobalCodeModal,
          setShowGlobalCodeModal,
        }}
      >
        {children}
        <audio className="hidden" ref={audioRef}></audio>
        {/* 会员不足一周弹窗 */}
        <ConfrimModal
          open={showVipExpireWarn}
          header={<div className="text-center">会员时长不足一周</div>}
          content={
            <div className="flex flex-col items-center">
              <span>您的会员剩余时长不足一周，请及时续费。</span>
              <label className="flex items-center mt-4 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={vipExpireNoTipToday}
                  onChange={(e) => setVipExpireNoTipToday(e.target.checked)}
                  className="mr-2"
                />
                今日不再提示
              </label>
            </div>
          }
          showFooter={true}
          onConfirm={() => {
            todayNoTip();
            setShowGlobalCodeModal(true);
          }}
          onCancel={() => {
            todayNoTip();
          }}
          confirmText="立即兑换"
        />
        {/* 确认弹窗 */}
        <ConfrimModal
          onConfirm={() => {
            setConfirmModal(false);
            confrimInfo?.onConfirm && confrimInfo?.onConfirm();
          }}
          onCancel={() => {
            setConfirmModal(false);
          }}
          showCancel={confrimInfo?.showCancel}
          showFooter={confrimInfo?.footer}
          header={confrimInfo?.title}
          content={confrimInfo?.message}
          open={confirmModal}
        />
        <LoginDialog
          open={loginModal!}
          handleClose={() => setLoginModal!(false)}
        />
        <RegisterDialog />
      </GlobalContext.Provider>

      {/* 全局弹窗 */}
      <Alert
        className="fixed bottom-0 md:bottom-5 right-0 md:right-5 w-full md:w-96 z-[99999] rounded-none sm:rounded-md"
        open={!!alertInfo}
        color={alertInfo?.color}
        onClose={() => setAlertInfo(undefined)}
        animate={{
          mount: { y: 0 },
          unmount: { y: 100 },
        }}
      >
        {alertInfo?.message || ""}
      </Alert>
    </>
  );
};

export const useGlobalData = () => {
  return useContext(GlobalContext);
};
